import { action, computed, makeObservable, observable } from 'mobx'

import LoginCommonStore from '~/client/src/login/stores/ui/LoginCommon.store'
import { UrlParamKey } from '~/client/src/shared/constants/commonRoutes'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ApiAuthService from '~/client/src/shared/services/ApiAuthService'
import AuthenticationStore from '~/client/src/shared/stores/domain/Authentication.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import {
  getReturnToFromUrl,
  getReturnToTenantHost,
} from '~/client/src/shared/utils/loginPortal'
import { getFormattedPhoneNumberForSubmission } from '~/client/src/shared/utils/phoneNumberHelpers'

import Config from '../../LoginConfig'
import { formatProviderList } from '../../providerDisplayNames'

// common for all tenants
export enum SignInMethod {
  PhoneNumber = 'phone-number',
  EmailPassword = 'email-password',
}

const relatedEvents = [e.LOGIN_WITH_INVITE_KEY, e.LOGIN_WITH_EMAIL_AND_PASSWORD]

export const OTPLength = 6

export default class LoginViewStore {
  @observable public errorMessage: string = ''
  @observable public signInMethod: SignInMethod = SignInMethod.EmailPassword

  @observable public shouldShowCodeInput: boolean = false
  @observable public isCodeBeingVerified: boolean = false
  @observable public codeVerificationErrorMsg: string = ''
  // undefined = not fetched, null = no restrictions, string[] = specific providers
  @observable public domainProviders: string[] | null | undefined = undefined

  public enteredPhoneNumber: string = ''
  public otcNonsense: string = ''
  private lastFetchedDomain: string = ''

  public constructor(
    protected readonly eventsStore: EventsStore,
    protected readonly auth: AuthenticationStore,
    protected readonly apiAuthService: ApiAuthService,
    protected readonly commonStore: LoginCommonStore,
  ) {
    this.signInMethod =
      (localStorage.getItem(
        LocalStorageKey.DefaultLoginMethod,
      ) as SignInMethod) || SignInMethod.EmailPassword
    makeObservable(this)
  }

  /**
   * Domain-level password check.
   * True when domain providers fetched and password is not among them.
   */
  @computed
  public get isPasswordBlockedByDomain(): boolean {
    if (this.domainProviders === undefined || this.domainProviders === null) {
      return false
    }
    return !this.domainProviders.some(p => p.toLowerCase() === 'password')
  }

  private getDomainBlockedMessage(): string {
    const providers = this.domainProviders || []
    return Localization.translator.passwordLoginNotAvailableForDomain(
      formatProviderList(providers),
    )
  }

  @action.bound
  public async onEmailBlur(email: string) {
    const domain = this.extractDomain(email)
    if (!domain || domain === this.lastFetchedDomain) return

    this.lastFetchedDomain = domain
    try {
      this.domainProviders =
        await this.apiAuthService.fetchDomainProviders(domain)

      // Show message if password blocked by domain
      if (this.isPasswordBlockedByDomain) {
        this.errorMessage = this.getDomainBlockedMessage()
      } else {
        this.resetErrorMessage()
      }
    } catch {
      // Fail open: auth enforces the domain's rules again at sign-in.
      this.domainProviders = undefined
    }
  }

  private extractDomain(email: string): string | null {
    if (!email?.includes('@')) return null
    const parts = email.trim().split('@')
    const domain = parts[1]?.toLowerCase()
    return domain?.includes('.') ? domain : null
  }

  @computed
  public get isLoading(): boolean {
    const { loading } = this.eventsStore.appState
    return relatedEvents.some(evt => loading.get(evt))
  }

  @action.bound
  public switchSignInMethod(newSignInMethod: SignInMethod) {
    this.resetErrorMessage()
    this.signInMethod = newSignInMethod
  }

  @action.bound
  public signInWithEmailPassword(email: string, password: string) {
    this.resetErrorMessage()

    if (this.isPasswordBlockedByDomain) {
      this.errorMessage = this.getDomainBlockedMessage()
      return
    }

    const trimmedEmail = email.trim()
    this.eventsStore.dispatch(
      e.LOGIN_WITH_EMAIL_AND_PASSWORD,
      trimmedEmail,
      password,
      this.loginSuccessful,
      this.onSignWithEmailPasswordError.bind(this, trimmedEmail),
    )
  }

  @action.bound
  public async signInWithPhoneNumber(phoneNumber: string) {
    this.resetErrorMessage()

    this.enteredPhoneNumber = phoneNumber
    await this.sendStxAuthOneTimeCode()
  }

  @action.bound
  public async sendStxAuthOneTimeCode() {
    if (!this.enteredPhoneNumber) {
      throw new Error('Phone number is required')
    }

    const phoneNumber = getFormattedPhoneNumberForSubmission(
      this.enteredPhoneNumber,
    )

    try {
      const response = await this.apiAuthService.sendOneTimeCode(phoneNumber)

      this.otcNonsense = response?.nonsense
      if (response?.error) {
        throw new Error(Localization.translator.errorSendingOneTimeCode)
      }

      if (!response?.nonsense) {
        throw new Error(Localization.translator.noNonsenseInOneTimeCodeResponse)
      }
      this.showVerificationCodeModal()
    } catch (error) {
      this.errorMessage = error.message
    }
  }

  @action.bound
  public async handleCodeInputComplete(otp: string) {
    this.isCodeBeingVerified = true

    try {
      await this.auth.loginWithPhoneOTP(
        this.enteredPhoneNumber,
        otp,
        this.otcNonsense,
      )
    } catch (e) {
      this.codeVerificationErrorMsg = e.message
      return
    } finally {
      this.isCodeBeingVerified = false
    }

    this.loginSuccessful()
  }

  @action.bound
  public handleCodeInputChange(value: string) {
    this.resetCodeVerificationErrorMessage()

    if (value.length >= OTPLength) {
      this.handleCodeInputComplete(value)
    }
  }

  @action.bound
  public showVerificationCodeModal() {
    this.shouldShowCodeInput = true
  }

  @action.bound
  public hideVerificationCodeModal() {
    this.shouldShowCodeInput = false
    this.codeVerificationErrorMsg = ''
    this.enteredPhoneNumber = ''
  }

  @action.bound
  public resetErrorMessage() {
    this.errorMessage = ''
  }

  @action.bound
  public resetCodeVerificationErrorMessage() {
    this.codeVerificationErrorMsg = ''
  }

  /**
   * Initiate Microsoft SSO via Auth service.
   * Redirects to Auth service's OAuth endpoint.
   */
  /**
   * Build the SSO callback URL for this Zoomin login origin, carrying the
   * post-login destination as `?return_to=` (STX1-540). The auth service
   * stores this redirectUri server-side keyed by the random `state` and
   * echoes it back on /sso-callback, so the destination survives the IdP
   * hop without ever reaching the IdP (only the opaque `state` does).
   */
  private buildSsoCallbackUrl(): string {
    const callbackUrl = `${window.location.origin}/sso-callback`
    const returnTo = getReturnToFromUrl()
    return returnTo
      ? `${callbackUrl}?${UrlParamKey.ReturnTo}=${encodeURIComponent(returnTo)}`
      : callbackUrl
  }

  @action.bound
  public async signInWithMicrosoftSSO() {
    this.resetErrorMessage()

    try {
      // Tenant is the return_to destination's host (STX1-540).
      const tenantHint = getReturnToTenantHost() || Config.TENANT_ID
      const ssoResponse = await this.apiAuthService.initiateMicrosoftSSO(
        this.buildSsoCallbackUrl(),
        tenantHint,
      )

      // Redirect to Microsoft login (state is validated server-side)
      window.location.href = ssoResponse.oAuthUrl
    } catch (error) {
      this.setSsoInitiationError('microsoft.com', error)
    }
  }

  /**
   * Initiate Procore SSO via Auth service.
   * Redirects to Auth service's OAuth endpoint.
   */
  @action.bound
  public async signInWithProcoreSSO() {
    this.resetErrorMessage()

    try {
      const tenantHint = getReturnToTenantHost() || Config.TENANT_ID
      const ssoResponse = await this.apiAuthService.initiateProcoreSSO(
        this.buildSsoCallbackUrl(),
        tenantHint,
      )

      // Redirect to Procore login (state is validated server-side)
      window.location.href = ssoResponse.oAuthUrl
    } catch (error) {
      this.setSsoInitiationError('procore.com', error)
    }
  }

  /**
   * Initiate Google SSO via Auth service.
   * Redirects to Auth service's OAuth endpoint.
   */
  @action.bound
  public async signInWithGoogleSSO() {
    this.resetErrorMessage()

    try {
      const tenantHint = getReturnToTenantHost() || Config.TENANT_ID
      const ssoResponse = await this.apiAuthService.initiateGoogleSSO(
        this.buildSsoCallbackUrl(),
        tenantHint,
      )

      // Redirect to Google login (state is validated server-side)
      window.location.href = ssoResponse.oAuthUrl
    } catch (error) {
      this.setSsoInitiationError('google.com', error)
    }
  }

  public loginSuccessful = () => {
    this.setDefaultLoginMethod()

    // Route uniformly to tenant routing (/projects → TenantRoutingView),
    // which applies the one rule (STX1-540): fly over to the return_to
    // destination, auto-select a single tenant, or show the picker.
    // displayAuthSuccessRoute carries return_to forward.
    this.commonStore.displayAuthSuccessRoute()
  }

  public goToResetPasswordView = (email: string) => {
    this.commonStore.displayResetPasswordView('', { email })
  }

  private onSignWithEmailPasswordError = (
    email: string,
    error: Error & { code?: string },
  ) => {
    this.setErrorMessage(error)

    if (error.code === 'too-many-requests') {
      this.eventsStore.dispatch(
        e.REQUEST_RESET_PASSWORD,
        email,
        () => {
          this.errorMessage =
            Localization.translator.tooManyUnsuccessfulLoginAttempts +
            ' ' +
            Localization.translator.passwordResetLinkWillBeSentIfAccountExists
        },
        (errorMsg: string) => (this.errorMessage = errorMsg),
      )
    }
  }

  private setDefaultLoginMethod = () => {
    localStorage.setItem(LocalStorageKey.DefaultLoginMethod, this.signInMethod)
  }

  private setErrorMessage(error: Error | { message: string }) {
    this.errorMessage = error.message
  }

  /**
   * SSO initiation failures carry the server's response body verbatim — for an
   * unhandled 5xx that is a raw exception message, which must never reach the
   * login form. Show a localized message and keep the detail in the console.
   */
  private setSsoInitiationError(providerId: string, error: unknown) {
    console.error(`SSO initiation failed for ${providerId}`, error)

    this.errorMessage = Localization.translator.ssoInitiationFailed
  }
}
